import {
  deserify,
  getLocalStorage,
  getTeacherSubjectName,
  Loader,
  PrimaryButton,
  pxToRem,
  pxTovW,
  SelectMenu,
  theme,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { Divider, Typography, useMediaQuery } from '@mui/material';
import { Box } from '@mui/system';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { SectionComp } from './SectionComp';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  setCurrentQPGTemplateInfo,
  setQPGGeneratedTemplates,
  setQPGSettingsConfig,
} from '../../../reducer/assessmentFlow.slice';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../reduxStore/reduxHooks';
import {
  QuestionPaperTemplate,
  QuestionSection,
} from '@protos/learning_management/lms.db_pb';
import { SaveTemplatePopup } from './SaveTemplatePopup';
import { setToastInfo } from '../../../../../pages/Home/reducer/homeDashboard.slice';
import { getQuestionEnumArrayFromQuestionTypes } from '../../../../../pages/Homework/ReviewHomework/functions';
import AddIcon from '@mui/icons-material/Add';
import { AiPromptTextEditor } from '../../../components/AirPromptTextEditor';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Question_QuestionEnum } from '@protos/content_management/content.db_pb';
import { useDownloadContext } from '../../../../../app/Context/DownloadContextProviderV2';
import { interactionEvent } from '../../../../Auth/auth.events';
import { ValidationPopup } from './ValidationPopup';
import { AssessmentCreationEnum } from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { getDifficultyLevelFromString } from '../../../Common/ReviewAssessment/functions';

interface IProps {
  isTemplateValid: boolean;
  setIsTemplateValid: React.Dispatch<React.SetStateAction<boolean>>;
  subjectId: number;
}
export const AddSection = (props: IProps) => {
  const { subjectId } = props;
  const { isTemplateValid, setIsTemplateValid } = props;
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  // const [sections, setSections] = useState(SectionList);
  const dragItem = useRef<number | null>(null);

  const [loading, setLoading] = useState<'loading' | 'completed' | 'error'>(
    'completed'
  );
  const {
    generated_qpg_templates,
    current_qpg_template_info,
    qpg_settings_config,
    qpg_selected_chapter_data,
  } = deserify(useAppSelector((state) => state.assessment));

  const availableQuestionTypes = qpg_settings_config?.allow_ai_questions
    ? [
        Question_QuestionEnum.QUESTION_TYPE_MCQS,
        Question_QuestionEnum.QUESTION_TYPE_MCQM,
        Question_QuestionEnum.QUESTION_TYPE_FIB,
        Question_QuestionEnum.QUESTION_TYPE_TF,
        Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT,
        Question_QuestionEnum.QUESTION_TYPE_SHORT,
        Question_QuestionEnum.QUESTION_TYPE_LONG,
        Question_QuestionEnum.QUESTION_TYPE_CBQ,
      ]
    : getQuestionEnumArrayFromQuestionTypes(
        qpg_settings_config?.no_ofQuestions_by_types || {}
      );
  const moduleIds =
    qpg_selected_chapter_data?.chapters
      .flatMap((chapter) => [
        chapter.chapterId,
        ...chapter.topics.map((topic) => topic.topicId),
      ])
      ?.sort((a, b) => a - b) || [];

  const filteredTemplates = qpg_settings_config?.allow_ai_questions
    ? generated_qpg_templates?.filter(
        (template) =>
          !template.questionSectionInfo.some(
            (section) =>
              section.questionType === Question_QuestionEnum.QUESTION_TYPE_CBQ
          )
      ) || []
    : generated_qpg_templates || [];

  const [displaySaveTemplatePopup, setDisplaySaveTemplatePopup] =
    useState<boolean>(false);
  const [validationStatus, setValidationStatus] = useState<
    { type: 'success' | 'error'; heading?: string; message: string } | undefined
  >(undefined);
  const teacher_id = getLocalStorage('userId');
  const dispatch = useAppDispatch();
  const [isTemplateEdited, setIsTemplateEdited] = useState(false);
  const [selectedSectionList, setSelectedSectionList] = useState<
    QuestionSection[]
  >([]);
  const { subject_id } = useParams();
  const { LmsAssessmentTeacherAPIServiceV1WithStatusCodeHandler } =
    useCommonServiceClientContext();

  const handleAddSection = async () => {
    setIsTemplateEdited(true);
    if (selectedSectionList.length > 0) {
      setSelectedSectionList([...selectedSectionList, new QuestionSection({})]);
    } else {
      setSelectedSectionList([new QuestionSection({})]);
    }
    dispatch(
      setCurrentQPGTemplateInfo({
        ...current_qpg_template_info,
        questionSectionInfo: selectedSectionList,
      })
    );
    await interactionEvent({
      context: 'Question_paper_generator',
      name: 'ADD_SECTION',
      pathSegments: pathSegments,
      isOffline: isOffline,
    });
  };

  const handleUpdateSection = (index: number, section: QuestionSection) => {
    setIsTemplateEdited(true);
    const updatedSections = [...selectedSectionList];
    updatedSections[index] = section;
    setSelectedSectionList(updatedSections);
    dispatch(
      setCurrentQPGTemplateInfo({
        ...current_qpg_template_info,
        questionSectionInfo: updatedSections,
      })
    );
    // console.log('Updated Section:', updatedSections);
    // console.log('selectedSectionList:', selectedSectionList);
  };
  const handleDeleteSection = (index: number) => {
    setIsTemplateEdited(true);
    const updatedSections = [...selectedSectionList];
    updatedSections.splice(index, 1);
    setSelectedSectionList(updatedSections);
    dispatch(
      setCurrentQPGTemplateInfo({
        ...current_qpg_template_info,
        questionSectionInfo: updatedSections,
      })
    );
  };
  const fetchSectionTemplates = async () => {
    try {
      setLoading('loading');
      const response =
        await LmsAssessmentTeacherAPIServiceV1WithStatusCodeHandler.getQuestionPaperTemplate(
          {
            personId: BigInt(teacher_id),
            subjectId: Number(subject_id),
          }
        );
      if (response.questionPaperTemplates) {
        dispatch(setQPGGeneratedTemplates(response.questionPaperTemplates));
      }
    } catch (error) {
      setLoading('error');
    } finally {
      setLoading('completed');
    }
  };
  const SaveSectionTemplates = async (templateName: string) => {
    try {
      const response =
        await LmsAssessmentTeacherAPIServiceV1WithStatusCodeHandler.createQuestionPaperTemplate(
          {
            teacherId: teacher_id,
            questionSectionInfo: selectedSectionList,
            templateTitle: templateName,
            subjectId: Number(subject_id),
          }
        );
      if (response.status === 200) {
        dispatch(
          setToastInfo({
            label: 'Section Template Saved Successfully',
            variant: 'success',
            open: true,
          })
        );
      } else {
        dispatch(
          setToastInfo({
            label: 'Section Template Save Failed',
            variant: 'error',
            open: true,
          })
        );
      }
    } catch (error) {
      dispatch(
        setToastInfo({
          label: 'Section Template Save Failed',
          variant: 'success',
          open: true,
        })
      );
    }
  };

  const totalMarks =
    current_qpg_template_info?.questionSectionInfo &&
    current_qpg_template_info.questionSectionInfo.length > 0
      ? current_qpg_template_info?.questionSectionInfo.reduce(
          (acc, section) =>
            acc + (section?.marks || 0) * (section?.noOfQuestion || 0),
          0
        )
      : 0;

  useEffect(() => {
    fetchSectionTemplates();
  }, [!selectedSectionList || selectedSectionList.length === 0]);
  useEffect(() => {
    dispatch(
      setQPGSettingsConfig({
        ...qpg_settings_config,
        prompt: undefined,
        prompt_validated: undefined,
      })
    );
  }, [selectedSectionList]);
  useEffect(() => {
    if (selectedSectionList && selectedSectionList?.length > 0) {
      if (selectedSectionList?.every((e) => e.noOfQuestion > 0)) {
        setIsTemplateValid(true);
      } else {
        setIsTemplateValid(false);
      }
    } else {
      setIsTemplateValid(false);
    }
  }, [selectedSectionList]);

  const onDragEnd = (result: any) => {
    if (!result.destination) return;
    const listCopy = [...selectedSectionList];
    const draggedItem = listCopy.splice(result.source.index, 1)[0];
    listCopy.splice(result.destination.index, 0, draggedItem);
    setSelectedSectionList(listCopy);
    dispatch(
      setCurrentQPGTemplateInfo({
        ...current_qpg_template_info,
        questionSectionInfo: listCopy,
      })
    );
  };

  return loading === 'loading' ? (
    <Loader />
  ) : (
    <Box
      sx={{
        // width: { xs: '100%', md: pxTovW(1250), lg: pxTovW(874) },
        mx: 'auto',
        // display: 'flex',
        // flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        mt: { xs: pxToRem(20), md: pxTovW(40) },
      }}
    >
      <Box
        sx={{
          // width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          marginY: { xs: pxToRem(13), md: pxTovW(21) },
        }}
      >
        <Typography variant="h2">
          <span style={{ color: '#007CDC' }}>Step 3: </span> Now Set Your
          Configuration Settings
        </Typography>
      </Box>

      {!!totalMarks ? (
        <Typography variant="h2" sx={{ color: '#007CDC' }}>
          {`Total: ${totalMarks} Marks`}
        </Typography>
      ) : null}

      <Box
        sx={{
          display: 'flex',
          justifyContent:
            selectedSectionList.length > 0 ? 'space-between' : 'center',
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', md: pxTovW(985) },
            boxSizing: 'border-box',
          }}
        >
          {selectedSectionList && selectedSectionList.length > 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                // width: { xs: '100%', md: pxTovW(1123) },
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  width: { xs: '100%', md: pxTovW(484), lg: pxTovW(394) },
                  gap: { xs: pxToRem(10), md: pxTovW(20) },
                }}
              >
                {!isTemplateEdited ? (
                  <SelectMenu
                    nofocus
                    placeholder="Select Paper Format"
                    value={current_qpg_template_info?.title || ''}
                    onChange={(e) => {
                      const template = generated_qpg_templates?.find(
                        (template) => template.title === e
                      );
                      if (template) {
                        const sectionList = template.questionSectionInfo.map(
                          (val) => {
                            return new QuestionSection({
                              ...val,
                              marks:
                                val.marks ||
                                (val.questionType &&
                                  !val.marks &&
                                  qpg_settings_config?.question_type_marks?.find(
                                    (e) => e.questionType === val.questionType
                                  )?.marks) ||
                                0,
                            });
                          }
                        );
                        setSelectedSectionList(sectionList);
                        dispatch(
                          setCurrentQPGTemplateInfo({
                            ...template,
                            questionSectionInfo: sectionList,
                          })
                        );
                      }
                    }}
                    optionList={
                      generated_qpg_templates?.map((template, index) => ({
                        name: template.title,
                        id: index,
                        nonEditableItem: false,
                      })) || []
                    }
                    rootStyles={{
                      height: {
                        xs: pxToRem(40),
                        md: pxTovW(70),
                        lg: pxTovW(56),
                      },
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #55ACFF',
                      '.MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&:focus-visible .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '& .MuiSelect-select > em': {
                        // backgroundColor: 'red',
                        fontStyle: 'normal !important',
                        fontFamily: 'Lato',
                      },
                      // borderRadius: { xs: pxToRem(15), md: pxTovW(15) },
                      width: { xs: '100%', md: pxTovW(484), lg: pxTovW(394) },
                    }}
                  />
                ) : (
                  <></>
                )}
              </Box>

              {/* <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: totalMarks ? 'space-between' : 'center',
              alignItems: 'center',
              marginTop: { xs: pxToRem(20), md: pxTovW(40) },
            }}
          >
            <Typography variant="h2" textAlign="center">
              Add Sections to the Question Paper
            </Typography>
            {!!totalMarks && (
              <Typography variant="h2" sx={{ color: '#007CDC' }}>
                {`Total: ${totalMarks} Marks`}
              </Typography>
            )}
          </Box> */}

              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="sections">
                  {(provided) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{ width: '100%' }}
                    >
                      {selectedSectionList.map((section, index) => (
                        <Draggable
                          key={'section_' + index}
                          draggableId={'section_' + index}
                          index={index}
                        >
                          {(provided) => (
                            <Box
                              key={index}
                              sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                textAlign: 'center',
                                // marginY: { xs: pxToRem(10), md: pxTovW(41) },
                                marginBottom: {
                                  xs: pxToRem(10),
                                  md: pxTovW(40),
                                },
                                cursor: 'grab',
                                background:
                                  dragItem.current === index
                                    ? '#EBF6FF'
                                    : 'transparent',
                              }}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              <SectionComp
                                index={index}
                                section={section}
                                updateSection={handleUpdateSection}
                                deleteSection={handleDeleteSection}
                              />
                            </Box>
                          )}
                        </Draggable>
                      ))}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>

              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'right',
                  gap: { xs: pxToRem(10), md: pxTovW(20) },
                }}
              >
                <PrimaryButton
                  sx={{
                    width: { xs: pxToRem(211), md: pxTovW(275) },
                    height: { xs: pxToRem(40), md: pxTovW(75) },
                    gap: { xs: pxToRem(10), md: pxTovW(10) },
                    backgroundColor: '#0AA34F',
                    '&:disabled': {
                      backgroundColor: '#0AA34F',
                      color: '#fff',
                      opacity: 0.5,
                    },
                  }}
                  disabled={!isTemplateValid}
                  onClick={async () => {
                    setDisplaySaveTemplatePopup(true);
                    await interactionEvent({
                      context: 'Question_paper_generator',
                      name: 'SAVE_TEMPLATE',
                      pathSegments: pathSegments,
                      isOffline: isOffline,
                    });
                  }}
                >
                  <AddIcon
                    sx={{
                      color: '#fff',
                      backgroundColor: '#044822',
                      fontSize: { xs: pxToRem(18), md: pxTovW(28) },
                      borderRadius: '50%',
                      p: '3px',
                    }}
                  />
                  <Typography
                    variant="h2"
                    fontWeight="bold"
                    color="white"
                    sx={{
                      fontSize: { xs: pxToRem(13), md: pxTovW(24) },
                    }}
                  >
                    SAVE TEMPLATE
                  </Typography>
                </PrimaryButton>
                <PrimaryButton
                  sx={{
                    width: { xs: pxToRem(211), md: pxTovW(275) },
                    height: { xs: pxToRem(40), md: pxTovW(75) },
                    gap: { xs: pxToRem(10), md: pxTovW(10) },
                    backgroundColor: '#007CDC',
                    '&:disabled': {
                      backgroundColor: '#007CDC',
                      color: '#fff',
                      opacity: 0.5,
                    },
                    '&:hover': {
                      backgroundColor: '#007CDC',
                    },
                  }}
                  disabled={
                    availableQuestionTypes.every((e) =>
                      selectedSectionList.find((s) => s.questionType === e)
                    ) || selectedSectionList.some((s) => s.noOfQuestion === 0)
                  }
                  onClick={() => handleAddSection()}
                >
                  <AddIcon
                    sx={{
                      color: '#fff',
                      backgroundColor: '#033661',
                      fontSize: { xs: pxToRem(18), md: pxTovW(28) },
                      borderRadius: '50%',
                      p: '3px',
                    }}
                  />
                  <Typography
                    variant="h2"
                    fontWeight="bold"
                    color="white"
                    sx={{
                      fontSize: { xs: pxToRem(13), md: pxTovW(24) },
                    }}
                  >
                    ADD SECTION
                  </Typography>
                </PrimaryButton>
              </Box>
            </Box>
          ) : (
            <AddSectionCTA
              addSectionClickHandler={() => handleAddSection()}
              selectTemplateHandler={(template: QuestionPaperTemplate) => {
                const sectionList = template.questionSectionInfo.map((val) => {
                  return new QuestionSection({
                    ...val,
                    marks:
                      val.marks ||
                      (val.questionType &&
                        !val.marks &&
                        qpg_settings_config?.question_type_marks?.find(
                          (e) => e.questionType === val.questionType
                        )?.marks) ||
                      0,
                  });
                });
                setSelectedSectionList(sectionList);
                dispatch(
                  setCurrentQPGTemplateInfo({
                    ...template,
                    questionSectionInfo: sectionList,
                  })
                );
              }}
            />
          )}
        </Box>

        {qpg_settings_config?.allow_ai_questions &&
        selectedSectionList &&
        selectedSectionList.length > 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: { xs: '100%', md: pxTovW(425) },
              justifyContent: 'center',
              alignItems: 'center',
              // mt: { xs: pxToRem(10), md: pxTovW(20) },
            }}
          >
            <AiPromptTextEditor
              value={qpg_settings_config?.prompt}
              validationEnabled={!!qpg_settings_config?.allow_ai_questions}
              validated={qpg_settings_config?.prompt_validated}
              onValidateClick={async () => {
                if (!qpg_settings_config?.prompt) {
                  return;
                }
                try {
                  setLoading('loading');
                  const res =
                    await LmsAssessmentTeacherAPIServiceV1WithStatusCodeHandler.checkValidPrompt(
                      {
                        subject:
                          getTeacherSubjectName(
                            subjectId,
                            user_info?.teachClassSubjects
                          ) || '',
                        moduleIds,
                        bloomLevels: qpg_settings_config?.bloomLevels || [],
                        questionSectionInfo: selectedSectionList,
                        avgDifficultyLevel: getDifficultyLevelFromString(
                          qpg_settings_config?.overallDifficultyLevel ||
                            'Medium'
                        ),
                        customPrompt: qpg_settings_config?.prompt,
                        teacherId: user_info?.teacherProfileId,
                        assessmentCreationType:
                          AssessmentCreationEnum.ASSESSMENT_CREATION_QUESTION_PAPER,
                      }
                    );
                  if (res.isValidated) {
                    dispatch(
                      setQPGSettingsConfig({
                        ...qpg_settings_config,
                        prompt_validated: 'success',
                      })
                    );
                    setValidationStatus({
                      type: 'success',
                      message: 'Prompt validated! Ready to use.',
                    });
                  } else {
                    dispatch(
                      setQPGSettingsConfig({
                        ...qpg_settings_config,
                        prompt_validated: 'error',
                      })
                    );
                    setValidationStatus({
                      type: 'error',
                      heading: 'Invalid Prompt',
                      message: (
                        (res.message || '') + ' Please review and try again.'
                      ).trim(),
                    });
                  }
                } catch (err) {
                  console.log(err);
                  setValidationStatus({
                    type: 'error',
                    heading: 'Some error occurred',
                    message:
                      'Error while validating prompt. Please try after some time',
                  });
                } finally {
                  setLoading('completed');
                }
              }}
              onChange={(val) => {
                dispatch(
                  setQPGSettingsConfig({
                    ...qpg_settings_config,
                    prompt: val,
                    prompt_validated: undefined,
                  })
                );
              }}
            />
          </Box>
        ) : (
          <></>
        )}
      </Box>

      <ValidationPopup
        open={!!validationStatus}
        handleClose={() => {
          setValidationStatus(undefined);
        }}
        okHandler={() => {
          setValidationStatus(undefined);
        }}
        iconname={
          validationStatus?.type == 'success'
            ? 'check-green'
            : validationStatus?.type == 'error'
            ? 'wrong'
            : undefined
        }
        text1={validationStatus?.heading}
        text2={validationStatus?.message}
      />

      <SaveTemplatePopup
        open={displaySaveTemplatePopup}
        handleClose={() => setDisplaySaveTemplatePopup(false)}
        saveTemplateHandler={(template) => {
          SaveSectionTemplates(template);
          fetchSectionTemplates();
        }}
      />
    </Box>
  );
};

interface IAddSectionProps {
  addSectionClickHandler: () => void;
  selectTemplateHandler: (template: QuestionPaperTemplate) => void;
}
const AddSectionCTA = (props: IAddSectionProps) => {
  const { addSectionClickHandler, selectTemplateHandler } = props;
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();
  const { generated_qpg_templates, qpg_settings_config } = deserify(
    useAppSelector((state) => state.assessment)
  );
  const ismobile = useMediaQuery(theme.breakpoints.down('md'));

  const filteredCTATemplates = qpg_settings_config?.allow_ai_questions
    ? generated_qpg_templates?.filter(
        (template) =>
          !template.questionSectionInfo.some(
            (section) =>
              section.questionType === Question_QuestionEnum.QUESTION_TYPE_CBQ
          )
      ) || []
    : generated_qpg_templates || [];
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: { xs: pxToRem(20), md: pxTovW(20) },
      }}
    >
      <Box
        sx={{
          // width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          marginY: { xs: pxToRem(23), md: pxTovW(41) },
        }}
      >
        <Typography variant="h2">Add Sections to the Question Paper</Typography>
      </Box>

      <PrimaryButton
        sx={{
          width: { xs: pxToRem(211), md: pxTovW(397) },
          height: { xs: pxToRem(48), md: pxTovW(77) },
          gap: { xs: pxToRem(10), md: pxTovW(10) },
          backgroundColor: '#007CDC',
          '&:hover': {
            backgroundColor: '#007CDC',
          },
        }}
        onClick={addSectionClickHandler}
      >
        <AddIcon
          sx={{
            color: '#fff',
            backgroundColor: '#033661',
            fontSize: { xs: pxToRem(20), md: pxTovW(30) },
            borderRadius: '50%',
            p: '3px',
          }}
        />
        <Typography
          variant={ismobile ? 'h4' : 'h2'}
          fontWeight="bold"
          color="white"
          sx={{
            fontSize: { xs: pxToRem(18), md: pxTovW(30), lg: pxTovW(24) },
          }}
        >
          ADD SECTION
        </Typography>
      </PrimaryButton>

      {generated_qpg_templates && generated_qpg_templates.length > 0 ? (
        <Divider
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '1px',
            marginY: { xs: pxToRem(10), md: pxTovW(20) },
            borderColor: '#007CDC',
            backgroundColor: '#007CDC',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: { xs: pxToRem(30), md: pxTovW(40) },
              height: { xs: pxToRem(30), md: pxTovW(40) },
              borderRadius: '50%',
              border: '1px solid #007CDC',
              backgroundColor: '#EBF6FF',
            }}
          >
            <Typography
              variant={ismobile ? 'cardText' : 'h2'}
              sx={{
                color: '#007CDC',
                fontSize: { xs: pxToRem(14), md: pxTovW(18) },
                fontWeight: 'bold',
                marginY: 'auto',
              }}
            >
              OR
            </Typography>
          </Box>
        </Divider>
      ) : (
        <></>
      )}
      {filteredCTATemplates && filteredCTATemplates.length > 0 ? (
        <Typography
          variant="h2"
          fontWeight="bold"
          sx={{
            textAlign: 'center',
            marginY: { xs: pxToRem(20), md: pxTovW(40) },
          }}
        >
          Use Ready Templates to Create Question Paper
        </Typography>
      ) : (
        <></>
      )}
      {filteredCTATemplates && filteredCTATemplates.length > 0 ? (
        <Box
          sx={{
            width: { xs: '100%', md: pxTovW(484), lg: pxTovW(394) },
          }}
        >
          {' '}
          <SelectMenu
            nofocus
            placeholder="Select Paper Format"
            value=""
            onChange={async (e) => {
              const template = filteredCTATemplates?.find(
                (template) => template.title === e
              );
              if (template) {
                selectTemplateHandler(template);
              }
              await interactionEvent({
                context: 'Use_ready_template',
                name: 'SELECT_PAPER_FORMAT',
                pathSegments: pathSegments,
                isOffline: isOffline,
              });
            }}
            optionList={
              filteredCTATemplates?.map((template, index) => ({
                name: template.title,
                id: index,
                nonEditableItem: false,
              })) || []
            }
            rootStyles={{
              height: { xs: pxToRem(40), md: pxTovW(70), lg: pxTovW(56) },
              backgroundColor: '#FFFFFF',
              border: '1px solid #55ACFF',
              '.MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:focus-visible .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '& .MuiSelect-select > em': {
                // backgroundColor: 'red',
                fontStyle: 'normal !important',
                fontFamily: 'Lato',
              },
              // borderRadius: { xs: pxToRem(15), md: pxTovW(15) },
              width: { xs: '100%', md: pxTovW(484), lg: pxTovW(394) },
            }}
            clearIcon={true}
          />
        </Box>
      ) : (
        <></>
      )}
    </Box>
  );
};
